import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, firstValueFrom, merge, of, take, takeUntil } from 'rxjs';
import { FirebaseError } from '@angular/fire/app';
import { Timestamp } from '@angular/fire/firestore';
import { isEqual } from 'lodash-es';
import { DateOnly, log } from '@nx-superprep/utils';

import {
  DayPlan, DayPlanModule, DayPlanAndModules, StudySession,
  StudyPhase, StudySetting, SpacedRepModuleConfig,
  StudyService, UserService, StudyOrderProgress,
  ApplicationSetting, DeckMode, isRegularStudyPhase
} from '@nx-superprep/backend/firebase';

import { ToolbarType } from '@nx-superprep/gameplay'

import { StudyStateService } from './study-state.service';
import { StudyAction } from './study-types'
import { StudySessionParams } from './study-session-params'

@Injectable({ providedIn: 'root'})
export class StudySessionService {
  private _isDailyReset = false;
  private _studyDone = new BehaviorSubject<boolean>(false);
  private _dayPlan!: DayPlan;
  private _dayPlanModule!: DayPlanModule;
  private _studySession!: StudySession;
  private _spacedRepConfig!: SpacedRepModuleConfig;
  private _studySetting!: StudySetting;
  private _appSetting!: ApplicationSetting;
  private _studyOrderProgress: StudyOrderProgress | undefined;
  private _moduleId: string | undefined;
  private _action: StudyAction = 'practice';
  private _onDestroy: Observable<boolean> = new Observable();
  private _toolbar: ToolbarType = { pause: true, progress: true, volume: true }

  private readonly userService = inject(UserService);
  private readonly studyService = inject(StudyService);
  private readonly stateService = inject(StudyStateService);

  initialize(params: StudySessionParams): void {
    this._moduleId = params.moduleId;
    this._action = params.action;
    this._onDestroy = params.onDestroy;

    if (!this._moduleId) { return; }

    this._studyOrderProgress = undefined;
    this._studyDone.next(false);

    this._observeApplicationSetting();
    if (isRegularStudyPhase(this.phase)) {
      this._observeStudyOrderProgress(this._moduleId);
    }
    this._observeDayPlan(this._moduleId);
  }

  get isDailyReset(): boolean {
    return this._isDailyReset;
  }

  set isDailyReset(value: boolean) {
    this._isDailyReset = value;
  }

  get studyDone(): BehaviorSubject<boolean> {
    return this._studyDone;
  }

  get dayPlan(): DayPlan {
    return this._dayPlan;
  }

  get dayPlanModule(): DayPlanModule {
    return this._dayPlanModule;
  }

  get studySession(): StudySession {
    return this._studySession;
  }

  get spacedRepConfig(): SpacedRepModuleConfig {
    return this._spacedRepConfig;
  }

  get studySetting(): StudySetting {
    return this._studySetting;
  }

  get appSetting(): ApplicationSetting {
    return this._appSetting;
  }

  set appSetting(value: ApplicationSetting) {
    this._appSetting = value;
    this.toolbar.editor = this._appSetting.contentReviewMode
    this.toolbar.debug = this._appSetting.debugMode
  }

  get toolbar(): ToolbarType {
    return this._toolbar;
  }

  get studyOrderProgress(): StudyOrderProgress | undefined {
    return this._studyOrderProgress;
  }

  get phase(): StudyPhase {
    switch(this._action) {
      case 'quiz': return StudyPhase.Quiz;
      case 'practice': return StudyPhase.Practice;
      default: return this._dayPlanModule ?
        (this._dayPlanModule.reviewProgress < this._dayPlanModule.reviewTarget ?
          StudyPhase.Review : StudyPhase.Acquisition)
        : StudyPhase.Review;
    }
  }

  private _observeApplicationSetting(): void {
    this.userService.getAppSetting()
    .pipe(
      catchError((val: FirebaseError) => {
        log.debug(val.stack);
        this.endOfStudy({action: 'exit', error: Error('failed fetching app setting')});
        return of();
      }),
      takeUntil(this._onDestroy),
    )
    .subscribe(setting => {
      this.appSetting = setting;
    });
  }

  private _observeStudyOrderProgress(id: string): void {
    this.studyService.getStudyOrderProgress(id)
    .pipe(takeUntil(this._onDestroy))
    .subscribe((studyOrderProgress) => {
      if (!studyOrderProgress.isValid || isEqual(this._studyOrderProgress, studyOrderProgress)) { return; }
      log.debug('study order progress', studyOrderProgress);
      this._studyOrderProgress = studyOrderProgress;
    });
  }

  private _observeDayPlan(moduleId: string): void {
    this.studyService.getDayPlanAndModules(new DateOnly())
    .pipe(
      catchError((val: FirebaseError) => {
        log.debug(val.stack);
        this.endOfStudy({action: 'exit', error: Error('failed fetching plan and module')});
        return of();
      }),
      take(1)
    )
    .subscribe(async (dayPlanAnModules: DayPlanAndModules|undefined) => {
      if (!dayPlanAnModules) {
        this.endOfStudy({action: 'exit', error: Error(`no plan and modules`)});
        return;
      }

      const [plan, modules] = [dayPlanAnModules.plan, dayPlanAnModules.modules];
      if (!plan.id) {
        this.endOfStudy({action: 'exit', error: Error('no plan id')});
        return;
      }

      if (isEqual([plan, modules], [this._dayPlan, this._dayPlanModule])) { return; }

      this._dayPlan = plan;
      const _isRegularStudyPhase = isRegularStudyPhase(this.phase);

      // Get module config
      const found = modules.findIndex(m => m.moduleId === moduleId);
      if (found < 0 && _isRegularStudyPhase) {
        this.endOfStudy({action: 'exit', error: Error(`no module ${moduleId} in plan ${plan.id}`)});
        return;
      }

      // Get day plan module for regular study or practice
      this._dayPlanModule = found < 0 ? new DayPlanModule({moduleId}) : modules[found];

      // Start a new session
      const session = await this.studyService.startStudySession(plan.id, moduleId);
      if (!session) {
        this.endOfStudy({action: 'exit', error: Error(`no session for ${moduleId} in plan ${plan.id}`)});
        return;
      }

      this._studySession = session;
      this.studyService.setActiveLesson(this.phase, plan, this._dayPlanModule, session);
      this._observeSpacedRepConfig(moduleId);
    });
  }

  private _observeSpacedRepConfig(moduleId: string): void {
    this.studyService.getSpacedRepConfig(moduleId)
    .pipe(take(1))
    .subscribe(spacedRepConfig => {
      this._spacedRepConfig = this._spacedRepConfig ?? new SpacedRepModuleConfig(spacedRepConfig);
      if (!this._spacedRepConfig.isValidConfiguration()) {
        this.endOfStudy({action: 'exit', error: Error(`invalid space rep config for ${moduleId}`)});
        return;
      }
      this._observeStudySetting();
    });
  }

  private _observeStudySetting(): void {
    this.studyService.getStudySetting()
    .pipe(takeUntil(merge(this.stateService.cancelNextCard)))
    .subscribe((setting) => {
      if (isEqual(this._studySetting, setting)) { return; } // no change

      // Track specific changes
      const durationChanged = this._studySetting && this._studySetting.duration !== setting.duration;
      this._studySetting = setting;

      if (durationChanged && !this._dayPlanModule.isAcquisitionFinished) {
        this.studyService.updateModuleTargets(this._dayPlanModule, setting.duration, this._spacedRepConfig);
      }

      log.debug(`session ${this._studySession.id} of ${this._dayPlanModule.id ?? this._moduleId} starts in ${this.phase}`);

      // Start study
      if (!this.stateService.isSessionTimerRunning()) {
        this.stateService.startSessionTimer();
      }
    });
  }

  // Helper function to handle common actions when ending the study
  private endOfStudyHelper(
    exitReason: string,
    plan: DayPlan,
    module: DayPlanModule,
    session: StudySession,
    studyOrderProgress: StudyOrderProgress | undefined
  ): Promise<void> {
    // If study is already done, return immediately
    if (this._studyDone.value) { return Promise.resolve(); }

    this._studyDone.next(true); // mark study done
    this.stateService.cancelNextCardOperation(); // cancel next card
    this.stateService.stopSessionTimer(); // stop session timer

    // Update plan module if available
    if (module?.id) {
      module.lastStudyDate = Timestamp.now();
      module.actualStudyTime = this.stateService.sessionTimerValue;
      module.isFinished = (this.phase === StudyPhase.Acquisition && module.isAcquisitionFinished && module.isReviewFinished);
      log.debug(`${module.moduleId} ${module.isFinished ? '' : 'not '}finished`);
    }

    // Set default exit reason
    exitReason ??= isRegularStudyPhase(this.phase)
    ? (this._isDailyReset || module?.isFinished || module?.progress >= 1 ? 'complete' : 'pause')
    : 'exit';

    // End study session
    const _endSession = () => this.studyService.endStudySession(this.phase, plan, module, session, studyOrderProgress)
    .catch(msg => {if (isRegularStudyPhase(this.phase)) { log.error(msg); } else { log.debug(msg); }});

    // Check if already saving, otherwise end session
    const _waitForIt = of(this.stateService.isSaving).pipe(
      filter(saving => !saving),
      take(1)
    );

    return firstValueFrom(_waitForIt).then(() => _endSession());
  }

  // End of study
  endOfStudy({
    action = 'pause',
    error = undefined,
    plan = this._dayPlan,
    planModule = this._dayPlanModule,
    session = this._studySession,
    studyOrderProgress = this._studyOrderProgress
  }: {
    action?: string,
    error?: string | Error,
    plan?: DayPlan,
    planModule?: DayPlanModule,
    session?: StudySession,
    studyOrderProgress?: StudyOrderProgress | undefined
  }): Promise<void> {
    // Cancel any ongoing operations
    this.stateService.cancelNextCardOperation();

    // Log error if present
    if (error) { if(error instanceof Error) { log.warn(error); } else { log.debug(error); }}

    return this.endOfStudyHelper(action, plan, planModule, session, studyOrderProgress);
  }

  createDeckModeRequest(
    moduleId: string,
    studyPhase: StudyPhase,
    deckMode: DeckMode = DeckMode.Deep,
    reviewCardIds: string[] = []
  ): {
    moduleId: string,
    studyPhase: StudyPhase,
    planDate: string,
    deckMode: DeckMode,
    reviewCardIds: string[]
  } {
    return {
      moduleId,
      studyPhase,
      planDate: this._dayPlan.dateOnly.toISOString(),
      deckMode,
      reviewCardIds
    };
  }
}
